import { OrbitActionCandidate } from '../../types';
import { ended, IDemoFlow, IDemoReply, performed, proposal, ruleTarget, step } from './types';

// Copy is the contract's own sample text for "deactivate rule ABC".

const deactivate = (id: number, name: string, now: number, ttlMs?: number): IDemoReply => ({
  outcome: 'action_proposed',
  answer: `I will deactivate the Rule ${name} (Rule ID: ${id}). Shall I proceed?`,
  action: proposal(
    step('deactivate_transaction_rule', 'Deactivate Rule', ruleTarget(id, name)),
    now,
    { ttlMs }
  )
});

const done = (id: number, name: string) => ({
  outcome: 'action_performed' as const,
  answer: `Deactivated the Rule ${name} (Rule ID: ${id}).`
});

const CANDIDATES: OrbitActionCandidate[] = [
  { step_id: 's1', id: 789, name: 'ABC Bonus', state: 'active', eligible: true, reason: null },
  {
    step_id: 's1',
    id: 790,
    name: 'ABC Launch',
    state: 'inactive',
    eligible: false,
    reason: 'inactive'
  },
  { step_id: 's1', id: 791, name: 'ABC Draft', state: 'draft', eligible: false, reason: 'draft' }
];

export const DEACTIVATE_RULE_FLOWS: IDemoFlow[] = [
  {
    id: 'deactivate',
    prompt: 'Deactivate rule ABC Bonus',
    reply: now => deactivate(789, 'ABC Bonus', now),
    confirm: action => ({
      ...done(789, 'ABC Bonus'),
      action: performed(action, { id: 789, state: 'inactive' })
    })
  },
  {
    id: 'deactivate-picker',
    prompt: 'Deactivate rule ABC',
    reply: () => ({
      outcome: 'clarify',
      answer: 'I found 3 rules named ABC. Which one?',
      action: {
        state: 'awaiting_selection',
        expires_at: null,
        steps: [
          {
            ...step('deactivate_transaction_rule', 'Deactivate Rule', {
              entity: 'rule',
              name: 'ABC',
              subtype: null
            })
          }
        ],
        candidates: CANDIDATES
      }
    }),
    select: (candidate, now) => deactivate(candidate.id, candidate.name, now),
    confirm: action => {
      const { id = 0, name } = action.steps[0].target;
      return { ...done(id, name), action: performed(action, { id, state: 'inactive' }) };
    }
  },
  {
    id: 'deactivate-pending',
    prompt: 'Deactivate rule Weekend Boost (maker-checker)',
    reply: now => deactivate(812, 'Weekend Boost', now),
    confirm: action => ({
      outcome: 'action_pending_approval',
      answer:
        'Your request to deactivate Rule Weekend Boost has been submitted and is awaiting approval.',
      action: ended(action, 'pending_approval')
    })
  },
  {
    id: 'deactivate-refused',
    prompt: 'Deactivate rule VIP Cashback (no permission)',
    reply: now => deactivate(733, 'VIP Cashback', now),
    confirm: action => ({
      outcome: 'action_refused',
      answer: "You don't have permission to deactivate rules.",
      action: ended(action, 'refused')
    })
  },
  {
    id: 'deactivate-unknown',
    prompt: 'Deactivate rule Holiday Promo (unknown result)',
    reply: now => deactivate(655, 'Holiday Promo', now),
    confirm: action => ({
      outcome: 'action_refused',
      answer:
        "I couldn't confirm that the Rule Holiday Promo (Rule ID: 655) was deactivated. Check the rule before trying again.",
      action: ended(action, 'failed', 'failed')
    })
  },
  {
    id: 'deactivate-410',
    prompt: 'Deactivate rule Old Promo (proposal replaced: 410)',
    reply: now => deactivate(501, 'Old Promo', now),
    confirm: () => 'expired'
  },
  {
    id: 'deactivate-expiring',
    prompt: 'Deactivate rule Flash Sale (expires in 20 s)',
    reply: now => deactivate(402, 'Flash Sale', now, 20 * 1000),
    confirm: action => ({
      ...done(402, 'Flash Sale'),
      action: performed(action, { id: 402, state: 'inactive' })
    })
  }
];
